import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { CircularProgress, Typography } from '@mui/material'
import CommonApi from '@/pages/api'
import Button from '@/src/components/button'
import { useStore } from '@/src/types/store'
import { TwoFactorEndpoints } from '@/pages/api/endpoints'
import TwoFactorForm from './TwoFactorForm'
import { formatSecret, type LoginResponse } from './loginFlow'

interface SetupData {
  secret: string
  issuer?: string
  account?: string
  otpauth_uri: string
}

export interface Props {
  mode: 'login' | 'profile'
  // login mode only — short-lived token from the login response. Never stored in a cookie.
  setupToken?: string
  onComplete: (response: LoginResponse) => void
  onCancel?: () => void
  compact?: boolean
}

type Step = 'intro' | 'qr' | 'confirm'

const TotpSetupWizard = ({ mode, setupToken, onComplete, onCancel, compact = false }: Props) => {
  const [step, setStep] = useState<Step>('intro')
  const [setup, setSetup] = useState<SetupData | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const { openToast } = useStore()

  // In login mode there is no access_token cookie yet, so CommonApi keeps the
  // header we pass here. In profile mode the cookie supplies the Bearer.
  const authHeader =
    mode === 'login' && setupToken ? { Authorization: `Bearer ${setupToken}` } : undefined

  const startSetup = async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        path: mode === 'login' ? TwoFactorEndpoints.loginSetup : TwoFactorEndpoints.profileSetup,
        method: 'post',
        headers: authHeader
      })
      if (response?.status && response?.data?.otpauth_uri) {
        setSetup(response.data)
        setStep('qr')
      } else {
        openToast(response?.msg || 'Could not start two-factor setup', 'error')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Could not start two-factor setup', 'error')
    } finally {
      setLoading(false)
    }
  }

  const confirmSetup = async (code: string) => {
    setLoading(true)
    try {
      const response = await CommonApi({
        path:
          mode === 'login'
            ? TwoFactorEndpoints.loginSetupConfirm
            : TwoFactorEndpoints.profileConfirm,
        method: 'post',
        headers: authHeader,
        data: { code }
      })
      if (response?.status) {
        onComplete(response)
      } else {
        openToast(response?.msg || 'Invalid verification code. Please try again.', 'error')
      }
    } catch (error: any) {
      openToast(
        error?.response?.data?.msg || 'Invalid verification code. Please try again.',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const copySecret = async () => {
    if (!setup?.secret) return
    try {
      await navigator.clipboard.writeText(setup.secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      openToast('Could not copy the key', 'error')
    }
  }

  if (step === 'confirm') {
    return (
      <TwoFactorForm
        title="Confirm your authenticator app"
        subtitle="Enter the 6-digit code your app is showing now."
        loading={loading}
        onVerify={confirmSetup}
        onBack={() => setStep('qr')}
        backText="Back"
      />
    )
  }

  if (step === 'qr' && setup) {
    return (
      <div className="flex flex-col items-center max-w-[26.5rem]">
        <Typography variant="h5" className="text-center">
          Scan this code
        </Typography>
        <Typography variant="body2" color="text.secondary" className="text-center pt-2">
          Open your authenticator app and scan the QR code below.
        </Typography>

        <div className="mt-6 p-3 bg-white rounded-[0.625rem] border border-[#E0E0E0]">
          <QRCodeSVG value={setup.otpauth_uri} size={compact ? 160 : 190} />
        </div>

        <Typography variant="body2" color="text.secondary" className="pt-6 text-center">
          Can’t scan? Enter this key manually:
        </Typography>
        <Typography
          variant="subtitle2"
          className="pt-1 text-center tracking-[0.15em] break-all"
          data-testid="automation-IRHQ_TwoFactor-Secret"
        >
          {formatSecret(setup.secret)}
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="body2"
          color="primary.main"
          className="cursor-pointer pt-1 font-semibold bg-transparent border-0"
          onClick={copySecret}
        >
          {copied ? 'Copied' : 'Copy key'}
        </Typography>

        <Button
          divClass="mt-8 w-full"
          text="Next"
          fullwidth={true}
          onClick={() => setStep('confirm')}
        />
        {onCancel && (
          <Typography
            component="button"
            type="button"
            variant="body2"
            color="primary.main"
            className="cursor-pointer pt-6 font-semibold bg-transparent border-0"
            onClick={onCancel}
          >
            Back to sign in
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center max-w-[26.5rem]">
      <Typography variant="h5" className="text-center">
        Set up two-factor authentication
      </Typography>
      <Typography variant="body2" color="text.secondary" className="text-center pt-2">
        Your account needs an authenticator app to sign in. Install Google Authenticator, Microsoft
        Authenticator, Authy or 1Password on your phone, then continue.
      </Typography>

      {loading ? (
        <div className="flex justify-center pt-10">
          <CircularProgress size={28} />
        </div>
      ) : (
        <Button divClass="mt-8 w-full" text="Continue" fullwidth={true} onClick={startSetup} />
      )}

      {onCancel && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          color="primary.main"
          className="cursor-pointer pt-6 font-semibold bg-transparent border-0"
          onClick={onCancel}
        >
          Back to sign in
        </Typography>
      )}
    </div>
  )
}

export default TotpSetupWizard
